// PayPal のボタン。
//
// ■ 注文はサーバが作る
// 金額と照合の印はサーバが決める。ここはボタンを出して、押されたら
// サーバに「注文を作って」「確定して」と頼むだけ。
//
// ■ 支払えたかどうかもサーバが決める
// 確定はサーバが PayPal に対して行い、その応答で判定する。
// ブラウザの「成功しました」は使わない。
//
// ■ 断られたとき
// サーバが `paypal_declined` を返したら、SDK の restart で別の手段を
// 選び直してもらう（公式の推奨どおり）。
import { useEffect, useRef, useState } from "react";
import { api } from "../api";

interface PaypalButtonsApi {
  Buttons: (opts: Record<string, unknown>) => {
    render: (el: HTMLElement) => Promise<void>;
    close?: () => Promise<void>;
    isEligible?: () => boolean;
  };
}
declare global {
  interface Window {
    paypal?: PaypalButtonsApi;
  }
}

interface ConfigResp {
  clientId: string;
  currency: string;
  amount: number;
  applicationId: string;
}

/**
 * SDK は 1 ページに 1 つ（Client ID ごと）。同じものを何度も足さない。
 * Client ID が変わったら（コミュニティの設定変更）読み直す。
 */
let sdkLoad: { clientId: string; promise: Promise<void> } | null = null;
function loadSdk(clientId: string, currency: string): Promise<void> {
  if (sdkLoad?.clientId === clientId) return sdkLoad.promise;
  const promise = new Promise<void>((resolve, reject) => {
    document
      .querySelectorAll('script[data-paypal-sdk="1"]')
      .forEach((s) => s.remove());
    const el = document.createElement("script");
    const q = new URLSearchParams({
      "client-id": clientId,
      currency,
      intent: "capture",
      components: "buttons",
    });
    el.src = `https://www.paypal.com/sdk/js?${q.toString()}`;
    el.async = true;
    el.dataset.paypalSdk = "1";
    el.onload = () => resolve();
    el.onerror = () => {
      sdkLoad = null; // 次に開いたときにやり直せるように
      reject(new Error("PayPal を読み込めませんでした"));
    };
    document.head.appendChild(el);
  });
  sdkLoad = { clientId, promise };
  return promise;
}

export function PaypalPayButton({
  applicationId,
  onPaid,
}: {
  applicationId: string;
  onPaid: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // 親が再描画するたびに関数が変わるので、ref で最新を持つ。
  // 依存に入れると、そのたびに PayPal のボタンを描き直してしまう。
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;

  useEffect(() => {
    let alive = true;
    let buttons: { close?: () => Promise<void> } | null = null;

    (async () => {
      try {
        const cfg = await api.get<ConfigResp>(
          `/payments/paypal/config/${applicationId}`,
        );
        await loadSdk(cfg.clientId, cfg.currency);
        if (!alive || !box.current || !window.paypal) return;

        // どのボタン（PayPal / カード等）が押されたか。注文の作り方だけに使う。
        let funding: string | undefined;
        const b = window.paypal.Buttons({
          style: { layout: "vertical", label: "pay" },
          onClick: (data: { fundingSource?: string }) => {
            funding = data.fundingSource;
            setErr(null);
            setMsg(null);
          },
          createOrder: async () => {
            const r = await api.post<{ id: string }>("/payments/paypal/orders", {
              applicationId,
              ...(funding ? { funding } : {}),
            });
            return r.id;
          },
          onApprove: async (
            data: { orderID: string },
            actions: { restart: () => Promise<void> },
          ) => {
            try {
              const r = await api.post<{ status: string }>(
                `/payments/paypal/orders/${encodeURIComponent(data.orderID)}/capture`,
                {},
              );
              if (r.status === "succeeded") {
                onPaidRef.current();
              } else {
                // 審査中など。確定したら自動で反映される（webhook）。
                setMsg(
                  "お支払いを受け付けました。確認でき次第、自動で反映されます。",
                );
              }
            } catch (e) {
              const code = (e as Error & { code?: string }).code;
              if (code === "already_paid") {
                // 別の手段・別の画面で先に払い終えていた。この注文は確定しない
                // （承認は期限で PayPal が取り消す）。表示を取り直す。
                onPaidRef.current();
                return;
              }
              if (code === "paypal_declined") {
                // 別の支払い手段を選び直してもらう（公式の推奨どおり）
                setErr((e as Error).message);
                return actions.restart();
              }
              setErr((e as Error).message);
            }
          },
          onCancel: () => setMsg("お支払いを中止しました。"),
          onError: () =>
            setErr("PayPal でエラーが起きました。時間をおいてお試しください。"),
        });
        buttons = b;
        if (b.isEligible && !b.isEligible()) {
          setErr("この環境では PayPal を使えません");
          return;
        }
        await b.render(box.current);
      } catch (e) {
        if (alive) setErr((e as Error).message);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      // 画面を離れたらボタンを片付ける（二重に描かない）
      buttons?.close?.().catch(() => {});
    };
  }, [applicationId]);

  return (
    <div className="space-y-2">
      {loading && <p className="text-xs text-tora-ink/60">PayPal を準備中…</p>}
      <div ref={box} className="max-w-sm" />
      {msg && <p className="text-sm text-tora-ink/80">{msg}</p>}
      {err && <p className="text-sm text-red-700">{err}</p>}
    </div>
  );
}
